/**
 * Enhanced Table View Renderer (Hypersort-style)
 * 
 * Features:
 * - Absolutely positioned rows for smooth animations
 * - Sorting with animated transitions
 * - Filtering with animated transitions
 * - Highlighting (rows, cells, columns, row ranges)
 * - Time Machine: Step-by-step timeline visualization
 * - Scroll-to functionality
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

interface TransformationStep {
  step: number;
  type: 'filter' | 'sort' | 'transform' | 'highlight';
  description: string;
  dataSnapshot: any[];
  // Detailed step information
  details?: {
    filter?: { column: string; operator: string; value: any };
    sort?: { column: string; direction: 'asc' | 'desc' };
    highlight?: { targetType: string; targetValue: any; action: string };
  };
}

interface TableState {
  data: any[];
  columns: string[];
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  filters: Array<{ column: string; operator: string; value: any }>;
  highlights: Array<{
    targetType: 'column' | 'row' | 'cell' | 'rowRange';
    targetValue: any;
    action: 'highlight' | 'scroll' | 'both';
  }>;
  transformationSteps: TransformationStep[];
  currentStep: number;
  isPlaying: boolean;
  playInterval?: number;
}

export const renderTableViewEnhanced: VisualizationRenderer = (data, meta) => {
  // Handle array of objects (most common case)
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Get column names from first object
  const columns = Object.keys(data[0]);
  
  if (columns.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Extract table configuration from meta.tableConfig
  const tableConfig = meta?.tableConfig || {};
  const initialFilters = tableConfig.filters || [];
  const initialSort = tableConfig.sort;
  const initialHighlights = tableConfig.highlights || [];
  const initialSteps = tableConfig.transformationSteps || [];

  // Build transformation steps with snapshots if not provided
  let transformationSteps: TransformationStep[] = [];
  if (initialSteps.length > 0) {
    // If steps come from backend, we need to compute snapshots
    // For now, use the current data as snapshot for all steps
    transformationSteps = initialSteps.map((step: any, index: number) => ({
      step: step.step ?? index,
      type: step.type,
      description: step.description,
      dataSnapshot: step.dataSnapshot || [...data], // Use current data if no snapshot
      details: step.details || extractStepDetails(step, initialFilters, initialSort, initialHighlights, index)
    }));
  } else {
    // Create initial step
    transformationSteps = [{
      step: 0,
      type: 'transform',
      description: 'Initial data',
      dataSnapshot: [...data],
      details: undefined
    }];
  }

  // Initialize state (stored in a data attribute on the container)
  const stateId = `table-state-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const initialState: TableState = {
    data: data,
    columns: columns,
    sortColumn: initialSort?.column,
    sortDirection: initialSort?.direction,
    filters: initialFilters,
    highlights: initialHighlights,
    transformationSteps: transformationSteps,
    currentStep: transformationSteps.length > 0 ? transformationSteps.length - 1 : 0,
    isPlaying: false
  };

  // Build table with absolutely positioned rows for smooth animations
  return `
    <div 
      id="${stateId}"
      data-state='${JSON.stringify(initialState)}'
      style="
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      ">
      ${renderControls(stateId)}
      ${renderTimeMachine(stateId)}
      ${renderTable(stateId, data, columns)}
      ${renderStyles()}
      ${renderScript(stateId, columns)}
    </div>
  `;
};

function renderControls(stateId: string): string {
  return `
    <div 
      class="table-controls"
      style="
        padding: ${designTokens.padding.md};
        border-bottom: 1px solid ${designTokens.borderColor};
        display: flex;
        gap: ${designTokens.padding.sm};
        flex-wrap: wrap;
        align-items: center;
      ">
      <div style="
        font-size: ${designTokens.fontSize.sm};
        font-weight: 600;
        color: ${designTokens.contentColor};
        margin-right: auto;
      ">Table Controls</div>
      <button 
        class="btn-filter"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          color: ${designTokens.contentColor};
          font-size: ${designTokens.fontSize.xs};
          cursor: pointer;
        ">Filter</button>
      <button 
        class="btn-clear-filters"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          color: ${designTokens.contentColor};
          font-size: ${designTokens.fontSize.xs};
          cursor: pointer;
        ">Clear Filters</button>
    </div>
  `;
}

function renderTimeMachine(stateId: string): string {
  return `
    <div 
      class="timemachine"
      style="
        padding: ${designTokens.padding.sm} ${designTokens.padding.md};
        border-bottom: 1px solid ${designTokens.borderColor};
        background: rgba(255, 255, 255, 0.02);
        display: flex;
        align-items: center;
        gap: ${designTokens.padding.sm};
      ">
      <div style="
        font-size: ${designTokens.fontSize.xs};
        color: ${designTokens.contentColorMuted};
        white-space: nowrap;
      ">Time Machine:</div>
      <div class="timemachine-steps" style="
        flex: 1;
        display: flex;
        gap: ${designTokens.padding.xs};
        overflow-x: auto;
        padding: ${designTokens.padding.xs} 0;
      "></div>
      <button 
        class="btn-prev-step"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          color: ${designTokens.contentColor};
          font-size: ${designTokens.fontSize.xs};
          cursor: pointer;
          white-space: nowrap;
        ">◀ Prev</button>
      <button 
        class="btn-play-pause"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(108, 204, 255, 0.2);
          border: 1px solid rgba(108, 204, 255, 0.4);
          border-radius: ${designTokens.radius.sm};
          color: ${designTokens.contentColor};
          font-size: ${designTokens.fontSize.xs};
          cursor: pointer;
          white-space: nowrap;
          font-weight: 600;
        ">▶ Play</button>
      <button 
        class="btn-next-step"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid ${designTokens.borderColor};
          border-radius: ${designTokens.radius.sm};
          color: ${designTokens.contentColor};
          font-size: ${designTokens.fontSize.xs};
          cursor: pointer;
          white-space: nowrap;
        ">Next ▶</button>
      <div 
        class="step-info"
        style="
          padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
          background: rgba(255, 255, 255, 0.05);
          border-radius: ${designTokens.radius.sm};
          font-size: ${designTokens.fontSize.xs};
          color: ${designTokens.contentColor};
          min-width: 200px;
          text-align: center;
        ">Step 0 of 0</div>
    </div>
  `;
}

function renderTable(stateId: string, data: any[], columns: string[]): string {
  const headerCells = columns.map(col => `
    <th 
      class="sortable-header"
      data-column="${escapeHtml(col)}"
      style="
        padding: ${designTokens.padding.sm} ${designTokens.padding.md};
        text-align: left;
        font-weight: 600;
        font-size: ${designTokens.fontSize.sm};
        color: ${designTokens.contentColor};
        border-bottom: 2px solid ${designTokens.borderColor};
        background: rgba(255, 255, 255, 0.05);
        cursor: pointer;
        user-select: none;
        position: relative;
        transition: background 0.2s;
      ">
      ${escapeHtml(col)}
      <span class="sort-indicator" style="
        margin-left: ${designTokens.padding.xs};
        opacity: 0;
        transition: opacity 0.2s;
      ">▼</span>
    </th>
  `).join('');

  // Build rows with data attributes for animation
  const rows = data.map((row, index) => {
    const rowId = `row-${index}`;
    const cells = columns.map((col, colIndex) => {
      const value = row[col];
      const displayValue = value === null || value === undefined 
        ? '<em style="color: rgba(255, 255, 255, 0.3);">—</em>'
        : escapeHtml(String(value));
      
      return `
        <td 
          class="table-cell"
          data-column="${escapeHtml(col)}"
          data-row-index="${index}"
          style="
            padding: ${designTokens.padding.sm} ${designTokens.padding.md};
            font-size: ${designTokens.fontSize.sm};
            color: ${designTokens.contentColor};
            border-bottom: 1px solid ${designTokens.borderColor};
            transition: background 0.3s, transform 0.3s;
          ">${displayValue}</td>
      `;
    }).join('');

    return `
      <tr 
        id="${rowId}"
        class="table-row"
        data-row-index="${index}"
        data-original-index="${index}"
        style="
          background: ${index % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)'};
          position: relative;
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), 
                      opacity 0.3s,
                      background 0.3s;
          will-change: transform, opacity;
        ">${cells}</tr>
    `;
  }).join('');

  return `
    <div 
      class="table-container"
      style="
        flex: 1;
        overflow: auto;
        position: relative;
      ">
      <table 
        class="data-table"
        style="
          width: 100%;
          border-collapse: collapse;
          font-size: ${designTokens.fontSize.sm};
          position: relative;
        ">
        <thead style="position: sticky; top: 0; z-index: 10; background: ${designTokens.cardBackground};">
          <tr>${headerCells}</tr>
        </thead>
        <tbody id="table-body-${stateId}">
          ${rows}
        </tbody>
      </table>
    </div>
  `;
}

function renderStyles(): string {
  return `
    <style>
      .table-row.highlighted {
        background: rgba(102, 204, 255, 0.2) !important;
        box-shadow: 0 0 8px rgba(102, 204, 255, 0.3);
      }
      
      .table-cell.highlighted {
        background: rgba(102, 204, 255, 0.3) !important;
        box-shadow: inset 0 0 4px rgba(102, 204, 255, 0.5);
      }
      
      .table-cell.column-highlighted {
        background: rgba(255, 200, 100, 0.15) !important;
      }
      
      .sortable-header:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
      
      .sortable-header.sort-asc .sort-indicator,
      .sortable-header.sort-desc .sort-indicator {
        opacity: 1;
      }
      
      .sortable-header.sort-desc .sort-indicator {
        transform: rotate(180deg);
      }
      
      .timemachine-step {
        padding: ${designTokens.padding.xs} ${designTokens.padding.sm};
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid ${designTokens.borderColor};
        border-radius: ${designTokens.radius.sm};
        font-size: ${designTokens.fontSize.xs};
        color: ${designTokens.contentColorMuted};
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.2s;
      }
      
      .timemachine-step:hover {
        background: rgba(255, 255, 255, 0.1);
      }
      
      .timemachine-step.active {
        background: rgba(102, 204, 255, 0.2);
        border-color: rgba(102, 204, 255, 0.5);
        color: ${designTokens.contentColor};
      }
      
      .table-row.animating {
        pointer-events: none;
      }
      
      @media (prefers-reduced-motion: reduce) {
        .table-row,
        .table-cell {
          transition: none !important;
        }
      }
    </style>
  `;
}

function renderScript(stateId: string, columns: string[]): string {
  return `
    <script>
      (function() {
        const container = document.getElementById('${stateId}');
        if (!container) {
          console.error('[TableEnhanced] Container not found:', '${stateId}');
          return;
        }
        
        console.log('[TableEnhanced] Initializing table with stateId:', '${stateId}');
        
        let state = JSON.parse(container.getAttribute('data-state') || '{}');
        const tbody = container.querySelector('#table-body-${stateId}');
        const timemachineSteps = container.querySelector('.timemachine-steps');
        const stepInfo = container.querySelector('.step-info');
        const playPauseBtn = container.querySelector('.btn-play-pause');
        
        console.log('[TableEnhanced] Found elements:', {
          tbody: !!tbody,
          timemachineSteps: !!timemachineSteps,
          stepInfo: !!stepInfo,
          playPauseBtn: !!playPauseBtn
        });
        
        // Update state helper
        function updateState(newState) {
          state = { ...state, ...newState };
          container.setAttribute('data-state', JSON.stringify(state));
        }
        
        // Get current filtered/sorted data
        function getCurrentData() {
          let result = [...state.data];
          
          // Apply filters
          for (const filter of state.filters) {
            result = applyFilter(result, filter);
          }
          
          // Apply sort
          if (state.sortColumn && state.sortDirection) {
            result = applySort(result, state.sortColumn, state.sortDirection);
          }
          
          return result;
        }
        
        // Apply filter
        function applyFilter(data, filter) {
          return data.filter(row => {
            const value = row[filter.column];
            switch (filter.operator) {
              case 'equals': return value == filter.value;
              case 'not_equals': return value != filter.value;
              case 'contains': return String(value).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'gt': return Number(value) > Number(filter.value);
              case 'lt': return Number(value) < Number(filter.value);
              case 'gte': return Number(value) >= Number(filter.value);
              case 'lte': return Number(value) <= Number(filter.value);
              default: return true;
            }
          });
        }
        
        // Apply sort
        function applySort(data, column, direction) {
          return [...data].sort((a, b) => {
            const aVal = a[column];
            const bVal = b[column];
            const aNum = parseFloat(aVal);
            const bNum = parseFloat(bVal);
            
            if (!isNaN(aNum) && !isNaN(bNum)) {
              return direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            
            const aStr = String(aVal || '');
            const bStr = String(bVal || '');
            return direction === 'asc' 
              ? aStr.localeCompare(bStr)
              : bStr.localeCompare(aStr);
          });
        }
        
        // Animate rows to new positions
        function animateRows(newData) {
          const rows = Array.from(tbody.querySelectorAll('.table-row'));
          const rowHeight = rows[0]?.offsetHeight || 40;
          
          // Create a map of original index to new index
          const indexMap = new Map();
          newData.forEach((newRow, newIndex) => {
            // Find original row index by matching data
            const originalIndex = state.data.findIndex(origRow => 
              JSON.stringify(origRow) === JSON.stringify(newRow)
            );
            if (originalIndex !== -1) {
              indexMap.set(originalIndex, newIndex);
            }
          });
          
          // Animate each row to its new position
          rows.forEach((row, currentIndex) => {
            const originalIndex = parseInt(row.getAttribute('data-original-index') || '0');
            const newIndex = indexMap.get(originalIndex);
            
            if (newIndex !== undefined && newIndex !== currentIndex) {
              row.classList.add('animating');
              const targetY = newIndex * rowHeight;
              
              // Use transform for smooth animation
              row.style.transform = \`translateY(\${targetY - (currentIndex * rowHeight)}px)\`;
              
              // After animation, update DOM order
              setTimeout(() => {
                row.style.transform = '';
                row.classList.remove('animating');
                // Reorder in DOM
                if (newIndex < rows.length) {
                  tbody.insertBefore(row, rows[newIndex] || null);
                }
              }, 400);
            }
          });
        }
        
        // Render table with current state
        function renderTable() {
          const currentData = getCurrentData();
          const rows = Array.from(tbody.querySelectorAll('.table-row'));
          
          // Update row visibility based on filters
          rows.forEach((row, index) => {
            const originalIndex = parseInt(row.getAttribute('data-original-index') || '0');
            const shouldShow = currentData.some(dataRow => 
              JSON.stringify(dataRow) === JSON.stringify(state.data[originalIndex])
            );
            
            if (shouldShow) {
              row.style.display = '';
              row.style.opacity = '1';
            } else {
              row.style.opacity = '0';
              setTimeout(() => {
                if (row.style.opacity === '0') {
                  row.style.display = 'none';
                }
              }, 300);
            }
          });
          
          // Animate sort
          if (state.sortColumn) {
            animateRows(currentData);
          }
          
          // Apply highlights
          applyHighlights();
        }
        
        // Apply highlights
        function applyHighlights() {
          // Clear existing highlights
          tbody.querySelectorAll('.highlighted').forEach(el => {
            el.classList.remove('highlighted');
          });
          
          for (const highlight of state.highlights) {
            if (highlight.targetType === 'row') {
              const rowIndex = highlight.targetValue;
              const row = tbody.querySelector(\`[data-row-index="\${rowIndex}"]\`);
              if (row) {
                row.classList.add('highlighted');
                if (highlight.action === 'scroll' || highlight.action === 'both') {
                  row.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            } else if (highlight.targetType === 'cell') {
              const { row, column } = highlight.targetValue;
              const cell = tbody.querySelector(\`[data-row-index="\${row}"][data-column="\${column}"]\`);
              if (cell) {
                cell.classList.add('highlighted');
                if (highlight.action === 'scroll' || highlight.action === 'both') {
                  cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }
            } else if (highlight.targetType === 'column') {
              const column = highlight.targetValue;
              tbody.querySelectorAll(\`[data-column="\${column}"]\`).forEach(cell => {
                cell.classList.add('column-highlighted');
              });
            } else if (highlight.targetType === 'rowRange') {
              const { start, end } = highlight.targetValue;
              for (let i = start; i <= end; i++) {
                const row = tbody.querySelector(\`[data-row-index="\${i}"]\`);
                if (row) row.classList.add('highlighted');
              }
            }
          }
        }
        
        // Get step description text
        function getStepDescription(step) {
          if (step.details) {
            if (step.details.filter) {
              const f = step.details.filter;
              const opNames = {
                'equals': '=',
                'not_equals': '≠',
                'contains': 'contains',
                'gt': '>',
                'lt': '<',
                'gte': '≥',
                'lte': '≤'
              };
              return \`Filter: \${f.column} \${opNames[f.operator] || f.operator} \${f.value}\`;
            } else if (step.details.sort) {
              const s = step.details.sort;
              return \`Sort: \${s.column} (\${s.direction === 'asc' ? 'ascending' : 'descending'})\`;
            } else if (step.details.highlight) {
              const h = step.details.highlight;
              if (h.targetType === 'column') {
                return \`Highlight: column "\${h.targetValue}"\`;
              } else if (h.targetType === 'cell') {
                return \`Highlight: cell [row \${h.targetValue.row}, col "\${h.targetValue.column}"]\`;
              } else if (h.targetType === 'row') {
                return \`Highlight: row \${h.targetValue}\`;
              } else if (h.targetType === 'rowRange') {
                return \`Highlight: rows \${h.targetValue.start}-\${h.targetValue.end}\`;
              }
            }
          }
          return step.description || \`\${step.type}\`;
        }
        
        // Update step info display
        function updateStepInfo() {
          if (!stepInfo) return;
          const step = state.transformationSteps[state.currentStep];
          if (step) {
            const total = state.transformationSteps.length;
            const description = getStepDescription(step);
            stepInfo.textContent = \`Step \${state.currentStep + 1} of \${total}: \${description}\`;
          } else {
            stepInfo.textContent = 'No steps';
          }
        }
        
        // Update timemachine steps
        function updateTimeMachine() {
          if (!timemachineSteps) return;
          
          timemachineSteps.innerHTML = state.transformationSteps.map((step, index) => {
            const isActive = index === state.currentStep;
            const description = getStepDescription(step);
            return \`
              <div 
                class="timemachine-step \${isActive ? 'active' : ''}"
                data-step="\${index}"
                style="cursor: pointer;"
                title="\${description}"
              >
                Step \${index}: \${step.type}
              </div>
            \`;
          }).join('');
          
          // Add click handlers
          timemachineSteps.querySelectorAll('.timemachine-step').forEach(stepEl => {
            stepEl.addEventListener('click', function() {
              const stepIndex = parseInt(this.getAttribute('data-step') || '0');
              console.log('[TableEnhanced] Step clicked:', stepIndex);
              goToStep(stepIndex);
            });
          });
          
          updateStepInfo();
        }
        
        // Go to specific step in timeline
        function goToStep(stepIndex) {
          console.log('[TableEnhanced] goToStep called:', stepIndex, 'of', state.transformationSteps.length);
          if (stepIndex < 0 || stepIndex >= state.transformationSteps.length) {
            console.warn('[TableEnhanced] Invalid step index:', stepIndex);
            return;
          }
          
          const step = state.transformationSteps[stepIndex];
          console.log('[TableEnhanced] Going to step:', step);
          
          // Reconstruct state from step
          let newFilters: any[] = [];
          let newSort: any = undefined;
          let newHighlights: any[] = [];
          
          // Apply all steps up to and including this step
          for (let i = 0; i <= stepIndex; i++) {
            const s = state.transformationSteps[i];
            if (s.details) {
              if (s.details.filter) {
                newFilters.push(s.details.filter);
              } else if (s.details.sort) {
                newSort = s.details.sort;
              } else if (s.details.highlight) {
                newHighlights.push(s.details.highlight);
              }
            }
          }
          
          // Update state
          updateState({
            currentStep: stepIndex,
            data: step.dataSnapshot ? [...step.dataSnapshot] : [...state.data],
            filters: newFilters,
            sortColumn: newSort?.column,
            sortDirection: newSort?.direction,
            highlights: newHighlights
          });
          
          console.log('[TableEnhanced] State updated:', {
            currentStep: state.currentStep,
            filters: state.filters.length,
            sort: state.sortColumn,
            highlights: state.highlights.length
          });
          
          // Re-render
          renderTable();
          updateTimeMachine();
        }
        
        // Play/pause functionality
        function togglePlay() {
          if (state.isPlaying) {
            // Pause
            if (state.playInterval) {
              clearInterval(state.playInterval);
              state.playInterval = undefined;
            }
            updateState({ isPlaying: false });
            if (playPauseBtn) {
              playPauseBtn.textContent = '▶ Play';
              playPauseBtn.style.background = 'rgba(108, 204, 255, 0.2)';
            }
          } else {
            // Play
            updateState({ isPlaying: true });
            if (playPauseBtn) {
              playPauseBtn.textContent = '⏸ Pause';
              playPauseBtn.style.background = 'rgba(255, 100, 100, 0.2)';
            }
            
            // Auto-advance every 1.5 seconds
            state.playInterval = setInterval(() => {
              if (state.currentStep < state.transformationSteps.length - 1) {
                goToStep(state.currentStep + 1);
              } else {
                // Reached end, pause
                togglePlay();
              }
            }, 1500);
          }
        }
        
        // Add transformation step
        function addTransformationStep(type, description, dataSnapshot) {
          const newStep = {
            step: state.transformationSteps.length,
            type: type,
            description: description,
            dataSnapshot: [...dataSnapshot]
          };
          
          updateState({
            transformationSteps: [...state.transformationSteps, newStep],
            currentStep: state.transformationSteps.length
          });
          
          updateTimeMachine();
        }
        
        // Sort handler
        container.querySelectorAll('.sortable-header').forEach(header => {
          header.addEventListener('click', function() {
            const column = this.getAttribute('data-column');
            const isAsc = this.classList.contains('sort-asc');
            const direction = isAsc ? 'desc' : 'asc';
            
            // Update sort state
            updateState({
              sortColumn: column,
              sortDirection: direction
            });
            
            // Update UI
            container.querySelectorAll('.sortable-header').forEach(h => {
              h.classList.remove('sort-asc', 'sort-desc');
            });
            this.classList.add(\`sort-\${direction}\`);
            
            // Add transformation step
            const currentData = getCurrentData();
            addTransformationStep('sort', \`Sort by \${column} \${direction}\`, currentData);
            
            // Re-render
            renderTable();
          });
        });
        
        // Time machine navigation - use setTimeout to ensure DOM is ready
        setTimeout(() => {
          const prevBtn = container.querySelector('.btn-prev-step');
          const nextBtn = container.querySelector('.btn-next-step');
          const playBtn = container.querySelector('.btn-play-pause');
          
          console.log('[TableEnhanced] Attaching event listeners:', {
            prevBtn: !!prevBtn,
            nextBtn: !!nextBtn,
            playBtn: !!playBtn
          });
          
          if (prevBtn) {
            prevBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[TableEnhanced] Prev button clicked');
              if (state.currentStep > 0) {
                goToStep(state.currentStep - 1);
              }
            });
          }
          
          if (nextBtn) {
            nextBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[TableEnhanced] Next button clicked');
              if (state.currentStep < state.transformationSteps.length - 1) {
                goToStep(state.currentStep + 1);
              }
            });
          }
          
          if (playBtn) {
            playBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('[TableEnhanced] Play/Pause button clicked');
              togglePlay();
            });
          }
          
          // Initialize
          console.log('[TableEnhanced] Initializing with', state.transformationSteps.length, 'steps');
          updateTimeMachine();
          renderTable();
        }, 0);
      })();
    </script>
  `;
}

/**
 * Extract step details from step description or config
 */
function extractStepDetails(
  step: any,
  filters: Array<{ column: string; operator: string; value: any }>,
  sort: { column: string; direction: 'asc' | 'desc' } | undefined,
  highlights: Array<any>,
  stepIndex: number
): TransformationStep['details'] {
  if (step.type === 'filter') {
    // Try to find matching filter
    const filterIndex = stepIndex - 1; // Assuming filters are applied in order
    if (filters[filterIndex]) {
      return { filter: filters[filterIndex] };
    }
  } else if (step.type === 'sort' && sort) {
    return { sort: sort };
  } else if (step.type === 'highlight') {
    // Try to find matching highlight
    const highlightIndex = stepIndex - (filters.length + (sort ? 1 : 0)) - 1;
    if (highlights[highlightIndex]) {
      return { highlight: highlights[highlightIndex] };
    }
  }
  return undefined;
}

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}

